import React, { useMemo, useState, useCallback } from "react";
import { Button, Table } from "react-bootstrap";
import RangeCell from "./RangeCell";
import MoneyCellInput from "./MoneyCellInput";
import AddTierPopover from "./AddTierPopover";
import {
    normalizeRangesEdit,
    splitLastInfiniteTier,
    updateTierEnd,
    splitTierToIsolateDay,
} from "../utils/tiers";

const SIMPLE_DAYS = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
    11, 12, 13, 14, 21, 30, 60, 90, 180, 360,
];

export default function PricingEditor({
    value = [],
    pricingUiMode = "SIMPLE",
    pricingMode = "FIXED",
    onChange,
    onChangeUiMode,
    popoverContainer,
}) {
    const seasonal = pricingMode === "SEASON";
    const [expanded, setExpanded] = useState(false);

    const prices = value || [];
    const uiMode = pricingUiMode || "SIMPLE";

    const setPrices = useCallback(
        (next) => {
            onChange?.(next);
        },
        [onChange]
    );

    const tiers = useMemo(
        () => normalizeRangesEdit(prices, seasonal),
        [prices, seasonal]
    );

    const onlyDay1Plus = tiers.length <= 1;

    const simpleRows = useMemo(() => {
        if (!tiers.length) return [];

        const rows = [];
        let ti = 0;

        for (const day of SIMPLE_DAYS) {
            while (ti < tiers.length - 1 && day > (tiers[ti].end ?? Infinity)) ti++;
            const t = tiers[ti];

            if (seasonal) {
                rows.push({
                    rentalDays: day,
                    low: Number(t.low ?? 0),
                    medium: Number(t.medium ?? 0),
                    high: Number(t.high ?? 0),
                });
            } else {
                rows.push({
                    rentalDays: day,
                    low: Number(t.low ?? 0),
                    medium: 0,
                    high: 0,
                });
            }
        }

        return rows;
    }, [tiers, seasonal]);

    const updateSimplePrice = useCallback(
        (day, field, valueCents) => {
            if (!tiers.length) return;

            if (!expanded && onlyDay1Plus && day === 1) {
                const base = { ...tiers[0] };

                if (seasonal) {
                    base[field] = valueCents;
                } else {
                    base.low = valueCents;
                }

                setPrices(normalizeRangesEdit([base], seasonal));
                return;
            }

            const idx = tiers.findIndex((t) => day >= t.start && (t.end == null || day <= t.end));
            if (idx === -1) return;

            const cur = tiers[idx];
            const parts = splitTierToIsolateDay(cur, day);

            const next = [
                ...tiers.slice(0, idx).map((t) => ({ ...t })),
                ...parts.map((t) => ({ ...t })),
                ...tiers.slice(idx + 1).map((t) => ({ ...t })),
            ];

            const midIdx = next.findIndex((t) => t.start === day && t.end === day);
            if (midIdx === -1) return;

            if (seasonal) {
                next[midIdx][field] = valueCents;
            } else {
                next[midIdx].low = valueCents;
            }

            setPrices(normalizeRangesEdit(next, seasonal));
        },
        [tiers, seasonal, expanded, onlyDay1Plus, setPrices]
    );

    const simpleHandlers = useMemo(() => {
        const map = new Map();

        if (seasonal) {
            for (const d of SIMPLE_DAYS) {
                map.set(`low-${d}`, (c) => updateSimplePrice(d, "low", c));
                map.set(`medium-${d}`, (c) => updateSimplePrice(d, "medium", c));
                map.set(`high-${d}`, (c) => updateSimplePrice(d, "high", c));
            }
        } else {
            for (const d of SIMPLE_DAYS) {
                map.set(`low-${d}`, (c) => updateSimplePrice(d, "low", c));
            }
        }

        return map;
    }, [seasonal, updateSimplePrice]);

    const patchRow = useCallback(
        (idx, patch) => {
            const next = tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t));
            setPrices(normalizeRangesEdit(next, seasonal));
        },
        [tiers, seasonal, setPrices]
    );

    const last = tiers[tiers.length - 1];
    const minCutDay = last?.start ?? 1;

    const onAddTier = useCallback(
        (cutDay) => {
            const next = splitLastInfiniteTier(tiers, cutDay, seasonal);
            setPrices(next);
        },
        [tiers, seasonal, setPrices]
    );

    const onRemoveTier = useCallback(
        (idx) => {
            if (idx <= 0) return;
            const next = tiers.filter((_, i) => i !== idx);
            setPrices(normalizeRangesEdit(next, seasonal));
        },
        [tiers, seasonal, setPrices]
    );

    const switchToAdvanced = useCallback(() => {
        onChangeUiMode?.("ADVANCED");
    }, [onChangeUiMode]);

    const switchToSimple = useCallback(() => {
        onChangeUiMode?.("SIMPLE");
    }, [onChangeUiMode]);

    const renderSimple = () => {
        const day1 = simpleRows[0] || { low: 0, medium: 0, high: 0 };

        if (!expanded && onlyDay1Plus) {
            return (
                <>
                    <div className="d-flex justify-content-between mb-2">
                        <div className="pricing-mode">Simple daily list</div>
                        <Button size="sm" variant="outline-secondary" onClick={switchToAdvanced}>
                            Advanced
                        </Button>
                    </div>

                    <Table className="configuration-table pricing-table">
                        <thead>
                            <tr>
                                <th>Duration</th>
                                {seasonal ? (
                                    <>
                                        <th>LOW</th>
                                        <th>MEDIUM</th>
                                        <th>HIGH</th>
                                    </>
                                ) : (
                                    <th>Daily rate</th>
                                )}
                            </tr>
                        </thead>

                        <tbody>
                            <tr>
                                <td className="tier-badge">Day 1+</td>

                                {seasonal ? (
                                    <>
                                        <td>
                                            <MoneyCellInput
                                                valueCents={day1.low}
                                                suffix="€"
                                                onChangeCents={simpleHandlers.get("low-1")}
                                            />
                                        </td>
                                        <td>
                                            <MoneyCellInput
                                                valueCents={day1.medium}
                                                suffix="€"
                                                onChangeCents={simpleHandlers.get("medium-1")}
                                            />
                                        </td>
                                        <td>
                                            <MoneyCellInput
                                                valueCents={day1.high}
                                                suffix="€"
                                                onChangeCents={simpleHandlers.get("high-1")}
                                            />
                                        </td>
                                    </>
                                ) : (
                                    <td>
                                        <MoneyCellInput
                                            valueCents={day1.low}
                                            suffix="€"
                                            onChangeCents={simpleHandlers.get("low-1")}
                                        />
                                    </td>
                                )}
                            </tr>

                            <tr>
                                <td colSpan={seasonal ? 4 : 2}>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted small">Offer discounts for longer rentals?</span>
                                        <Button size="sm" variant="outline-primary" onClick={() => setExpanded(true)}>
                                            Add duration discounts
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </>
            );
        }

        return (
            <>
                <div className="d-flex justify-content-between mb-2">
                    <div className="pricing-mode">Simple daily list</div>
                    <Button size="sm" variant="outline-secondary" onClick={switchToAdvanced}>
                        Advanced
                    </Button>
                </div>

                <Table className="configuration-table pricing-table">
                    <thead>
                        <tr>
                            <th>Rental days</th>
                            {seasonal ? (
                                <>
                                    <th>LOW</th>
                                    <th>MEDIUM</th>
                                    <th>HIGH</th>
                                </>
                            ) : (
                                <th>Daily rate</th>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {simpleRows.map((r) => (
                            <tr key={r.rentalDays}>
                                <td className="tier-badge">Day {r.rentalDays}</td>

                                {seasonal ? (
                                    <>
                                        <td>
                                            <MoneyCellInput
                                                valueCents={r.low}
                                                suffix="€"
                                                onChangeCents={simpleHandlers.get(`low-${r.rentalDays}`)}
                                            />
                                        </td>
                                        <td>
                                            <MoneyCellInput
                                                valueCents={r.medium}
                                                suffix="€"
                                                onChangeCents={simpleHandlers.get(`medium-${r.rentalDays}`)}
                                            />
                                        </td>
                                        <td>
                                            <MoneyCellInput
                                                valueCents={r.high}
                                                suffix="€"
                                                onChangeCents={simpleHandlers.get(`high-${r.rentalDays}`)}
                                            />
                                        </td>
                                    </>
                                ) : (
                                    <td>
                                        <MoneyCellInput
                                            valueCents={r.low}
                                            suffix="€"
                                            onChangeCents={simpleHandlers.get(`low-${r.rentalDays}`)}
                                        />
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </>
        );
    };

    const renderAdvanced = () => {
        return (
            <>
                <div className="d-flex justify-content-between mb-2">
                    <div className="pricing-mode">{seasonal ? "3 seasons pricing" : "Single price"}</div>
                    <Button size="sm" variant="outline-secondary" onClick={switchToSimple}>
                        Simple
                    </Button>
                </div>

                <Table className="configuration-table pricing-table">
                    <thead>
                        <tr>
                            <th>Duration</th>
                            {seasonal ? (
                                <>
                                    <th>LOW</th>
                                    <th>MEDIUM</th>
                                    <th>HIGH</th>
                                    <th />
                                </>
                            ) : (
                                <>
                                    <th>Daily rate</th>
                                    <th />
                                </>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {tiers.map((r, idx) => {
                            const isLast = r.end == null;

                            return (
                                <tr key={r._id}>
                                    <td>
                                        <RangeCell
                                            range={r}
                                            idx={idx}
                                            isLast={isLast}
                                            onChangeEnd={(rowIdx, nextEnd) => {
                                                const next = updateTierEnd(tiers, rowIdx, nextEnd, seasonal);
                                                setPrices(next);
                                            }}
                                        />
                                    </td>

                                    {seasonal ? (
                                        <>
                                            <td>
                                                <MoneyCellInput
                                                    valueCents={r.low ?? 0}
                                                    suffix="€"
                                                    onChangeCents={(c) => patchRow(idx, { low: c })}
                                                />
                                            </td>
                                            <td>
                                                <MoneyCellInput
                                                    valueCents={r.medium ?? 0}
                                                    suffix="€"
                                                    onChangeCents={(c) => patchRow(idx, { medium: c })}
                                                />
                                            </td>
                                            <td>
                                                <MoneyCellInput
                                                    valueCents={r.high ?? 0}
                                                    suffix="€"
                                                    onChangeCents={(c) => patchRow(idx, { high: c })}
                                                />
                                            </td>
                                        </>
                                    ) : (
                                        <td>
                                            <MoneyCellInput
                                                valueCents={r.low ?? 0}
                                                suffix="€"
                                                onChangeCents={(c) => patchRow(idx, { low: c })}
                                            />
                                        </td>
                                    )}

                                    <td>
                                        {idx > 0 && (
                                            <Button size="sm" variant="outline-danger" onClick={() => onRemoveTier(idx)}>
                                                Remove
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>

                    <tfoot>
                        <tr>
                            <td colSpan={seasonal ? 5 : 2}>
                                <AddTierPopover
                                    disabled={!tiers.some((t) => t.end == null)}
                                    minCutDay={minCutDay}
                                    container={popoverContainer}
                                    onConfirm={onAddTier}
                                />
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </>
        );
    };

    return uiMode === "ADVANCED" ? renderAdvanced() : renderSimple();
}